/*
 * theme.js — global behaviour (vanilla, no dependencies).
 *
 * window.Cart            Ajax cart helper (see CONVENTIONS.md)
 *   Cart.get()                         -> Promise<cart>
 *   Cart.add(items, opts)              -> Promise<cart>   items: {id, quantity, properties} or an array of them
 *   Cart.change(keyOrLine, quantity)   -> Promise<cart>   key (string) or 1-based line (number)
 *   Cart.update(updates, extra)        -> Promise<cart>   updates: {variantIdOrKey: qty}
 *   Cart.clear()                       -> Promise<cart>
 *   Cart.formatMoney(cents)            -> "$129" / "$129.50"
 * Events on document:
 *   cart:updated  detail {cart, source, added}   after every successful mutation (and Cart.get with {announce:true})
 *   cart:open     ask the cart drawer to open (dispatched after Cart.add unless opts.open === false,
 *                 and when a [data-cart-toggle] is clicked while a [data-cart-drawer] exists)
 *   cart:error    detail {message, response}
 */
(function () {
  'use strict';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var url = function (path) { return root + path.replace(/^\//, ''); };
  var emit = function (name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  };

  /* ------------------------------------------------------------------ */
  /* Cart                                                                */
  /* ------------------------------------------------------------------ */
  function request(path, body) {
    var opts = { credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
    if (body !== undefined) {
      opts.method = 'POST';
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(url(path), opts).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) {
          var message = (data && (data.description || data.message)) || (window.themeStrings && window.themeStrings.cartError) || 'Cart error';
          var err = new Error(message);
          err.response = data;
          emit('cart:error', { message: message, response: data });
          throw err;
        }
        return data;
      });
    });
  }

  var Cart = {
    state: null,
    get: function (opts) {
      return request('cart.js').then(function (cart) {
        Cart.state = cart;
        if (opts && opts.announce) emit('cart:updated', { cart: cart, source: 'get' });
        return cart;
      });
    },
    add: function (items, opts) {
      opts = opts || {};
      var list = (Array.isArray(items) ? items : [items]).map(function (i) {
        var item = { id: Number(i.id), quantity: i.quantity == null ? 1 : Number(i.quantity) };
        if (i.properties && Object.keys(i.properties).length) item.properties = i.properties;
        if (i.selling_plan) item.selling_plan = i.selling_plan;
        return item;
      });
      return request('cart/add.js', { items: list }).then(function (added) {
        return request('cart.js').then(function (cart) {
          Cart.state = cart;
          emit('cart:updated', { cart: cart, source: 'add', added: added.items || [added] });
          if (opts.open !== false) emit('cart:open', { cart: cart });
          return cart;
        });
      });
    },
    change: function (keyOrLine, quantity, extra) {
      var body = Object.assign({ quantity: Number(quantity) }, extra || {});
      if (typeof keyOrLine === 'number') body.line = keyOrLine; else body.id = String(keyOrLine);
      return request('cart/change.js', body).then(function (cart) {
        Cart.state = cart;
        emit('cart:updated', { cart: cart, source: 'change' });
        return cart;
      });
    },
    update: function (updates, extra) {
      var body = Object.assign({}, extra || {});
      if (updates) body.updates = updates;
      return request('cart/update.js', body).then(function (cart) {
        Cart.state = cart;
        emit('cart:updated', { cart: cart, source: 'update' });
        return cart;
      });
    },
    clear: function () {
      return request('cart/clear.js', {}).then(function (cart) {
        Cart.state = cart;
        emit('cart:updated', { cart: cart, source: 'clear' });
        return cart;
      });
    },
    formatMoney: function (cents) {
      var n = Number(cents || 0) / 100;
      var s = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/\.00$/, '');
      var fmt = (window.themeSettings && window.themeSettings.moneyFormat) || '${{amount}}';
      return fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, s).replace(/<[^>]+>/g, '');
    }
  };
  window.Cart = Cart;

  // keep every [data-cart-count] in sync
  document.addEventListener('cart:updated', function (e) {
    var cart = e.detail && e.detail.cart;
    if (!cart) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  });

  // cart icon: open the drawer when one exists, otherwise follow the link to /cart
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-cart-toggle]');
    if (!toggle) return;
    if (!document.querySelector('[data-cart-drawer]')) return;
    if (document.body.classList.contains('template-cart')) return;
    e.preventDefault();
    emit('cart:open', {});
  });

  /* ------------------------------------------------------------------ */
  /* Header: mobile menu                                                 */
  /* ------------------------------------------------------------------ */
  function initMenu() {
    var menu = document.querySelector('[data-mobile-menu]');
    if (!menu) return;
    var openBtn = document.querySelector('[data-menu-open]');
    var lastFocus = null;
    var pushed = false;
    window.addEventListener('popstate', function () { if (pushed) { pushed = false; close(true); } });
    function open() {
      lastFocus = document.activeElement;
      try { window.history.pushState({ mobileMenu: true }, ''); pushed = true; } catch (e) { pushed = false; }
      menu.hidden = false;
      requestAnimationFrame(function () { menu.classList.add('is-open'); });
      document.documentElement.classList.add('has-overlay');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      var first = menu.querySelector('button, a');
      if (first) first.focus();
    }
    function close(fromHistory) {
      if (fromHistory !== true && pushed) { pushed = false; try { window.history.back(); } catch (e) { /* ignore */ } }
      menu.classList.remove('is-open');
      document.documentElement.classList.remove('has-overlay');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      setTimeout(function () { menu.hidden = true; }, 320);
      if (lastFocus) lastFocus.focus();
    }
    if (openBtn) openBtn.addEventListener('click', open);
    menu.addEventListener('click', function (e) {
      if (e.target.closest('[data-menu-close]') || e.target === menu) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !menu.hidden) close();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Announcement bar: rotate messages                                   */
  /* ------------------------------------------------------------------ */
  function initAnnouncement() {
    document.querySelectorAll('[data-announcement]').forEach(function (bar) {
      var items = bar.querySelectorAll('[data-announcement-item]');
      if (items.length < 2) return;
      var speed = (parseInt(bar.getAttribute('data-speed'), 10) || 5) * 1000;
      var index = 0;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) return;
      // rotates through the messages twice, then rests on the first one (no endless motion)
      var steps = 0;
      var max = items.length * 2;
      var timer = setInterval(function () {
        if (bar.matches(':hover') || bar.contains(document.activeElement)) return;
        items[index].classList.remove('is-active');
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
        steps++;
        if (steps >= max) clearInterval(timer);
      }, speed);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Sticky header shadow                                                */
  /* ------------------------------------------------------------------ */
  function initHeaderScroll() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    // only the header stays stuck: the sticky wrapper is pulled up by the announcement's height
    var bar = document.querySelector('[data-announcement]');
    var wrap = header.closest('.section-header');
    var measure = function () {
      if (wrap) wrap.style.setProperty('--announce-h', (bar ? bar.offsetHeight : 0) + 'px');
    };
    measure();
    window.addEventListener('resize', measure);
    var onScroll = function () { header.classList.toggle('is-scrolled', window.scrollY > 8); };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ------------------------------------------------------------------ */
  /* Accordions: [data-accordion-single] closes siblings                 */
  /* ------------------------------------------------------------------ */
  function initAccordions() {
    document.querySelectorAll('[data-accordion-single]').forEach(function (group) {
      group.addEventListener('toggle', function (e) {
        var item = e.target;
        if (!item.open) return;
        group.querySelectorAll('details[open]').forEach(function (other) {
          if (other !== item) other.open = false;
        });
      }, true);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Tabs: [data-tabs] > [data-tab="id"] buttons + [data-tab-panel="id"] */
  /* ------------------------------------------------------------------ */
  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach(function (wrap) {
      var tabs = wrap.querySelectorAll('[data-tab]');
      var panels = wrap.querySelectorAll('[data-tab-panel]');
      function select(id, focus) {
        tabs.forEach(function (t) {
          var on = t.getAttribute('data-tab') === id;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          if (on && focus) t.focus();
        });
        panels.forEach(function (p) {
          var on = p.getAttribute('data-tab-panel') === id;
          p.hidden = !on;
          p.classList.toggle('is-active', on);
        });
      }
      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { select(tab.getAttribute('data-tab')); });
        tab.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
          e.preventDefault();
          var dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
          var next = tabs[(i + dir + tabs.length) % tabs.length];
          select(next.getAttribute('data-tab'), true);
        });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Client-side filter chips: [data-filter-group]                       */
  /*   chips: [data-filter-value="courage"] ("" = all)                   */
  /*   items: [data-filter-item] with data-theme="courage"               */
  /* ------------------------------------------------------------------ */
  function initFilters() {
    document.querySelectorAll('[data-filter-group]').forEach(function (group) {
      var attr = group.getAttribute('data-filter-attr') || 'theme';
      var chips = group.querySelectorAll('[data-filter-value]');
      var items = group.querySelectorAll('[data-filter-item]');
      var count = group.querySelector('[data-filter-count]');
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var value = chip.getAttribute('data-filter-value');
          var shown = 0;
          chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
          items.forEach(function (item) {
            var card = item.matches('[data-' + attr + ']') ? item : item.querySelector('[data-' + attr + ']');
            var match = !value || (card && card.getAttribute('data-' + attr) === value);
            item.hidden = !match;
            if (match) shown++;
          });
          if (count) count.textContent = shown;
        });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Reveal on scroll (progressive: content is visible without JS)       */
  /* ------------------------------------------------------------------ */
  function initReveal() {
    var els = document.querySelectorAll('[data-reveal]');
    if (!els.length || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.documentElement.classList.add('reveal-ready');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach(function (el) { io.observe(el); });
    // safety: never leave content hidden
    setTimeout(function () { els.forEach(function (el) { el.classList.add('is-revealed'); }); }, 4000);
  }

  /* ------------------------------------------------------------------ */
  /* Marquee pause button + one-row scrollers                            */
  /* ------------------------------------------------------------------ */
  function initMarquee() {
    document.querySelectorAll('[data-marquee-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrap = btn.closest('.marquee');
        var paused = wrap.classList.toggle('is-paused');
        btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      });
    });
  }
  function initScrollNav() {
    document.querySelectorAll('[data-scroll-nav]').forEach(function (nav) {
      var row = document.getElementById(nav.getAttribute('data-scroll-nav'));
      if (!row) return;
      var prev = nav.querySelector('[data-scroll-prev]');
      var next = nav.querySelector('[data-scroll-next]');
      var step = function () { var first = row.firstElementChild; return first ? first.getBoundingClientRect().width + parseFloat(getComputedStyle(row).columnGap || 0) : row.clientWidth; };
      var update = function () {
        var max = row.scrollWidth - row.clientWidth - 2;
        prev.disabled = row.scrollLeft <= 2;
        next.disabled = row.scrollLeft >= max;
        nav.hidden = row.scrollWidth <= row.clientWidth + 2;
      };
      prev.addEventListener('click', function () { row.scrollBy({ left: -step(), behavior: 'smooth' }); });
      next.addEventListener('click', function () { row.scrollBy({ left: step(), behavior: 'smooth' }); });
      row.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Mega menu (desktop): opens on hover or click, closes on leave / Esc */
  /* ------------------------------------------------------------------ */
  function initMega() {
    var header = document.querySelector('[data-header]');
    var toggle = document.querySelector('[data-mega-toggle]');
    var panel = document.querySelector('[data-mega-panel]');
    if (!header || !toggle || !panel) return;
    var closeTimer = null;
    function open() {
      clearTimeout(closeTimer);
      panel.hidden = false;
      header.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    }
    function close() {
      panel.hidden = true;
      header.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }
    toggle.addEventListener('click', function () { if (panel.hidden) open(); else close(); });
    if (window.matchMedia('(hover: hover)').matches) {
      toggle.addEventListener('mouseenter', open);
      header.addEventListener('mouseleave', function () { closeTimer = setTimeout(close, 180); });
      header.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
      header.querySelectorAll('.header__nav a, .header__logo a, .header__actions a').forEach(function (el) { el.addEventListener('mouseenter', close); });
    }
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !panel.hidden) { close(); toggle.focus(); } });
    document.addEventListener('click', function (e) { if (!panel.hidden && !header.contains(e.target)) close(); });
    header.addEventListener('focusout', function (e) { if (!panel.hidden && e.relatedTarget && !header.contains(e.relatedTarget)) close(); });
  }

  /* ------------------------------------------------------------------ */
  /* Reveal stage: drawing over photo, driven by a range input           */
  /* ------------------------------------------------------------------ */
  function initReveal2() {
    var stages = document.querySelectorAll('[data-reveal-stage]');
    if (!stages.length) return;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    stages.forEach(function (stage) {
      var range = stage.querySelector('[data-reveal-range]');
      if (!range) return;
      var set = function (v) {
        v = Math.max(0, Math.min(100, v));
        stage.style.setProperty('--reveal', v + '%');
        range.value = v;
      };
      var stop = function () { stage._touched = true; if (stage._timer) { clearInterval(stage._timer); stage._timer = null; } };
      set(50);
      // keyboard (and assistive tech) through the range input
      range.addEventListener('input', function () { stop(); stage.style.setProperty('--reveal', range.value + '%'); });
      // pointer and touch anywhere on the stage
      var fromEvent = function (e) { var r = stage.getBoundingClientRect(); set((e.clientX - r.left) / r.width * 100); };
      var dragging = false;
      stage.addEventListener('dragstart', function (e) { e.preventDefault(); });
      stage.addEventListener('pointerdown', function (e) {
        if (e.button !== undefined && e.button !== 0) return;
        dragging = true; stop(); fromEvent(e);
        if (stage.setPointerCapture) { try { stage.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ } }
      });
      stage.addEventListener('pointermove', function (e) { if (dragging) fromEvent(e); });
      ['pointerup', 'pointercancel'].forEach(function (n) { stage.addEventListener(n, function () { dragging = false; }); });
      // plays once when scrolled into view: 50 -> 15 -> 85 -> rests at 50
      stage._play = function () {
        if (reduce || stage._played || stage._touched) return;
        stage._played = true;
        var keys = [50, 15, 85, 50];
        var dur = 2400;
        var start = Date.now();
        stage._timer = setInterval(function () {
          var p = Math.min(1, (Date.now() - start) / dur);
          var seg = p * (keys.length - 1);
          var i = Math.min(keys.length - 2, Math.floor(seg));
          var local = seg - i;
          var eased = local < 0.5 ? 2 * local * local : 1 - Math.pow(-2 * local + 2, 2) / 2;
          set(keys[i] + (keys[i + 1] - keys[i]) * eased);
          if (p >= 1) { clearInterval(stage._timer); stage._timer = null; set(50); }
        }, 16);
      };
    });
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.target._play) { entry.target._play(); }
        });
      }, { threshold: 0.35 });
      stages.forEach(function (stage) { io.observe(stage); });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Footer wordmark: fit the brand name to the full width               */
  /* ------------------------------------------------------------------ */
  function initFitMark() {
    var mark = document.querySelector('.footer__mark');
    if (!mark) return;
    var fit = function () {
      mark.style.fontSize = '';
      var avail = mark.clientWidth - parseFloat(getComputedStyle(mark).paddingLeft) - parseFloat(getComputedStyle(mark).paddingRight);
      var span = document.createElement('span');
      span.style.cssText = 'display:inline-block;white-space:nowrap';
      span.textContent = mark.textContent;
      mark.textContent = ''; mark.appendChild(span);
      var size = parseFloat(getComputedStyle(mark).fontSize);
      var w = span.getBoundingClientRect().width;
      if (w > 0) mark.style.fontSize = (size * avail / w) + 'px';
      mark.textContent = span.textContent;
    };
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit); else fit();
    window.addEventListener('resize', fit);
  }

  function init() {
    initMega();
    initReveal2();
    initFitMark();
    initMarquee();
    initScrollNav();
    initMenu();
    initAnnouncement();
    initHeaderScroll();
    initAccordions();
    initTabs();
    initFilters();
    initReveal();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* Creator video (story-reveal): muted loop that plays only while on screen, never with reduced motion; pause/play button. */
(function () {
  'use strict';
  var init = function () {
    document.querySelectorAll('[data-creator-video]').forEach(function (video) {
      var btn = video.parentNode.querySelector('[data-creator-toggle]');
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var userPaused = reduce;
      var setBtn = function () {
        if (!btn) return;
        var playing = !video.paused;
        btn.setAttribute('aria-pressed', playing ? 'false' : 'true');
        btn.setAttribute('aria-label', playing ? btn.getAttribute('data-label-pause') : btn.getAttribute('data-label-play'));
        btn.classList.toggle('is-paused', !playing);
      };
      var play = function () { var p = video.play(); if (p && p.catch) p.catch(function () {}); };
      video.addEventListener('play', setBtn); video.addEventListener('pause', setBtn);
      if (btn) btn.addEventListener('click', function () {
        if (video.paused) { userPaused = false; play(); } else { userPaused = true; video.pause(); }
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting && !userPaused) play(); else if (!e.isIntersecting && !video.paused) video.pause();
          });
        }, { threshold: 0.25 }).observe(video);
      } else if (!userPaused) { play(); }
      setBtn();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
